import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type { WellnessSubscription, WellnessTokens, WellnessUser, StripeProduct, StripeCoupon, AddParticipantRequest } from '@/types/wellness';
import * as wellnessService from '@/services/api/wellnessService';
import { wellnessApiClient } from '@/services/api/wellnessApiClient';
import { isTokenExpired } from '@/features/wellness/utils/jwtUtils';
import { clearSentryUser } from '@/utils/sentry';
import { reset as mixpanelReset } from '@/utils/mixpanel';
import axios from 'axios';

type SubscriptionStatus = 'idle' | 'loading' | 'success' | 'error';
type ProductsStatus = 'idle' | 'loading' | 'success' | 'error';

export interface PendingUpgradeCheckout {
  path: string;
  priceId?: string;
  couponId?: string;
  useFreeTrial?: boolean;
}

interface WellnessState {
  tokens: WellnessTokens | null;
  email: string | null;
  session: string | null;
  redirectPath: string | null;
  pendingParticipantRequest: AddParticipantRequest | null;
  /** Resume subscribe/checkout after completing profile (non-B2C → B2C conversion) */
  pendingUpgradeCheckout: PendingUpgradeCheckout | null;
  subscriptionErrorMessage: string | null;
  user: WellnessUser | null;
  needsProfile: boolean;
  b2c: boolean;
  subscription: WellnessSubscription | null;
  subscriptionStatus: SubscriptionStatus;
  subscriptionError: string | null;
  products: StripeProduct[];
  coupons: StripeCoupon[];
  productsStatus: ProductsStatus;
  productsError: string | null;
  /** True when logged in via B2B support "login as" (support-exchange); survives token refresh */
  supportSession: boolean;
  setSession: (session: string | null) => void;
  setEmail: (email: string | null) => void;
  setRedirectPath: (redirectPath: string | null) => void;
  setPendingParticipantRequest: (request: AddParticipantRequest | null) => void;
  setPendingUpgradeCheckout: (pending: PendingUpgradeCheckout | null) => void;
  setSubscriptionErrorMessage: (message: string | null) => void;
  setAuthData: (data: {
    tokens?: WellnessTokens | null;
    user?: WellnessUser | null;
  }) => void;
  setSupportSession: (active: boolean) => void;
  clearAuth: () => void;
  isIdTokenExpired: () => boolean;
  isRefreshTokenExpired: () => boolean;
  fetchSubscriptionStatus: () => Promise<void>;
  fetchProducts: () => Promise<void>;
  updateProfile: (payload: { firstName: string; lastName: string; birthDate: string }) => Promise<void>;
  getSubscriptionPortalUrl: (returnUrl: string) => Promise<string>;
  createCheckoutSession: (
    priceId: string,
    successUrl: string,
    cancelUrl: string,
    couponId?: string,
    useFreeTrial?: boolean
  ) => Promise<import('@/types/wellness').SubscriptionCheckoutResponse>;
  activateB2C: () => Promise<void>;
}

export const useWellnessStore = create<WellnessState>()(
  devtools(
    persist(
      (set, get) => ({
        tokens: null,
        email: null,
        session: null,
        redirectPath: null,
        pendingParticipantRequest: null,
        pendingUpgradeCheckout: null,
        subscriptionErrorMessage: null,
        user: null,
        needsProfile: false,
        b2c: false,
        subscription: null,
        subscriptionStatus: 'idle',
        subscriptionError: null,
        products: [],
        coupons: [],
        productsStatus: 'idle',
        productsError: null,
        supportSession: false,
        setSession: (session) => set({ session }),
        setEmail: (email) => set({ email }),
        setRedirectPath: (redirectPath) => set({ redirectPath }),
        setPendingParticipantRequest: (request) => set({ pendingParticipantRequest: request }),
        setPendingUpgradeCheckout: (pending) => set({ pendingUpgradeCheckout: pending }),
        setSubscriptionErrorMessage: (message) => set({ subscriptionErrorMessage: message }),
        setAuthData: ({ tokens, user }) =>
          set((state) => ({
            tokens: tokens ?? state.tokens,
            user: user ?? state.user,
            email: user?.email ?? state.email,
            needsProfile: user ? user.needsProfile ?? false : state.needsProfile,
            b2c: user ? Boolean(user.b2c) : state.b2c,
          })),
        setSupportSession: (active) => set({ supportSession: active }),
        clearAuth: () => {
          // Clear user identification in Sentry and Mixpanel
          clearSentryUser();
          mixpanelReset();

          set({
            tokens: null,
            email: null,
            session: null,
            redirectPath: null,
            pendingParticipantRequest: null,
            pendingUpgradeCheckout: null,
            subscriptionErrorMessage: null,
            user: null,
            needsProfile: false,
            b2c: false,
            subscription: null,
            subscriptionStatus: 'idle',
            subscriptionError: null,
            products: [],
            productsStatus: 'idle',
            productsError: null,
            supportSession: false,
          });
        },
        isIdTokenExpired: () => {
          const { tokens } = get();
          if (!tokens?.idToken) {
            return true;
          }
          
          // Check expiration using stored timestamp if available
          if (tokens.idTokenExpiresAt) {
            return Date.now() >= tokens.idTokenExpiresAt;
          }
          
          // Fallback to JWT decoding if timestamp not available
          return isTokenExpired(tokens.idToken);
        },
        isRefreshTokenExpired: () => {
          const { tokens } = get();
          if (!tokens?.refreshToken) {
            return true;
          }
          
          // Check expiration using stored timestamp if available
          if (tokens.refreshTokenExpiresAt) {
            return Date.now() >= tokens.refreshTokenExpiresAt;
          }
          
          // Try to decode JWT to check expiration
          // If refresh token doesn't have exp claim, assume it's valid
          return isTokenExpired(tokens.refreshToken);
        },
        fetchSubscriptionStatus: async () => {
          const { tokens } = get();
          if (!tokens?.idToken) {
            set({
              subscriptionStatus: 'error',
              subscriptionError: 'Missing authentication token',
            });
            return;
          }

          set({ subscriptionStatus: 'loading', subscriptionError: null });
          try {
            const subscription = await wellnessService.fetchSubscriptionStatus();
            // subscription can be null if 404 (no subscription found) - this is a valid state
            set({
              subscription,
              subscriptionStatus: 'success',
              subscriptionError: null,
            });
          } catch (error) {
            // Error handling is done by wellnessApiClient interceptor
            console.error('[WellnessStore] fetchSubscriptionStatus error', error);
            set({
              subscription: null,
              subscriptionStatus: 'error',
              subscriptionError: error instanceof Error ? error.message : 'Unable to load subscription',
            });
          }
        },
        fetchProducts: async () => {
          // The /self/subscription/getproducts endpoint is public, so we can fetch without authentication
          set({ productsStatus: 'loading', productsError: null });
          try {
            const { products, coupons } = await wellnessService.fetchProducts();
            set({
              products,
              coupons,
              productsStatus: 'success',
              productsError: null,
            });
          } catch (error) {
            // Error handling is done by wellnessApiClient interceptor
            console.error('[WellnessStore] fetchProducts error', error);
            set({
              products: [],
              productsStatus: 'error',
              productsError: error instanceof Error ? error.message : 'Unable to load products',
            });
          }
        },
        updateProfile: async (payload) => {
          const { tokens } = get();
          if (!tokens?.idToken) {
            throw new Error('Missing authentication token');
          }

          try {
            await wellnessApiClient.patch('/self/auth/profile', payload);

            set((state) => ({
              user: state.user
                ? { ...state.user, ...payload, needsProfile: false }
                : { ...payload, email: state.email ?? '', needsProfile: false },
              needsProfile: false,
            }));
          } catch (error) {
            // Error handling is done by wellnessApiClient interceptor
            if (axios.isAxiosError(error)) {
              const errorData = error.response?.data as { message?: string } | undefined;
              throw new Error(errorData?.message || 'Failed to update profile');
            }
            throw error;
          }
        },
        getSubscriptionPortalUrl: async (returnUrl: string) => {
          const { tokens } = get();
          if (!tokens?.idToken) {
            throw new Error('Missing authentication token');
          }
          return wellnessService.getPortalUrl({ returnUrl });
        },
        createCheckoutSession: async (
          priceId: string,
          successUrl: string,
          cancelUrl: string,
          couponId?: string,
          useFreeTrial?: boolean
        ) => {
          const { tokens } = get();
          if (!tokens?.idToken) {
            throw new Error('Missing authentication token');
          }
          return wellnessService.createCheckoutSession({
            priceId,
            successUrl,
            cancelUrl,
            couponId,
            useFreeTrial,
          });
        },
        activateB2C: async () => {
          const { tokens, needsProfile, user } = get();
          if (!tokens?.idToken) {
            throw new Error('Missing authentication token');
          }
          if (
            needsProfile ||
            !user?.firstName?.trim() ||
            !user?.lastName?.trim() ||
            !user?.birthDate
          ) {
            throw new Error('Profile must be completed before activating B2C');
          }

          try {
            const { user: updatedUser } = await wellnessService.activateB2C();
            // Update user state with the returned user object
            set((state) => ({
              user: updatedUser,
              email: updatedUser?.email ?? state.email,
              needsProfile: updatedUser ? updatedUser.needsProfile ?? false : state.needsProfile,
              b2c: Boolean(updatedUser?.b2c),
            }));
          } catch (error) {
            console.error('[WellnessStore] activateB2C error', error);
            throw error;
          }
        },
      }),
      {
        name: 'wellness-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          tokens: state.tokens,
          email: state.email,
          session: state.session,
          redirectPath: state.redirectPath,
          pendingParticipantRequest: state.pendingParticipantRequest,
          pendingUpgradeCheckout: state.pendingUpgradeCheckout,
          subscriptionErrorMessage: state.subscriptionErrorMessage,
          user: state.user,
          needsProfile: state.needsProfile,
          b2c: state.b2c,
          subscription: state.subscription,
          supportSession: state.supportSession,
        }),
      }
    ),
    {
      name: 'wellness-store',
      enabled: import.meta.env.DEV // Only enable devtools in development
    }
  )
);

export const getWellnessState = () => useWellnessStore.getState();
